// Presentational primitives for the scored Fatigue dashboard.
//
// The prototype's .kpi/.pill/.card/.chip/.fbar/.sec-hdr CSS classes, rebuilt as
// Tailwind components on the app's shadcn tokens (success/warning/destructive/
// info) so the dashboard follows the active brand + light/dark mode instead of
// the prototype's hard-coded dark palette.
//
// Tone mapping from the prototype: teal → success, amber → warning, red →
// destructive, blue → info.

import React from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'violet' | 'muted';

export const toneText: Record<Tone, string> = {
  good: 'text-success',
  warn: 'text-warning',
  bad: 'text-destructive',
  info: 'text-info',
  violet: 'text-violet',
  muted: 'text-muted-foreground',
};
export const toneBg: Record<Tone, string> = {
  good: 'bg-success',
  warn: 'bg-warning',
  bad: 'bg-destructive',
  info: 'bg-info',
  violet: 'bg-violet',
  muted: 'bg-muted-foreground',
};
/** Tinted surface + border, used for pills, alert KPIs and banners. */
export const toneSurface: Record<Tone, string> = {
  good: 'border-success/30 bg-success/10 text-success',
  warn: 'border-warning/30 bg-warning/10 text-warning',
  bad: 'border-destructive/30 bg-destructive/10 text-destructive',
  info: 'border-info/30 bg-info/10 text-info',
  violet: 'border-violet/30 bg-violet/10 text-violet',
  muted: 'border-border bg-muted text-muted-foreground',
};
/** CSS colour for inline styles / recharts (SVG can't use Tailwind classes). */
export const toneVar: Record<Tone, string> = {
  good: 'var(--color-success)',
  warn: 'var(--color-warning)',
  bad: 'var(--color-destructive)',
  info: 'var(--color-info)',
  violet: 'var(--color-violet)',
  muted: 'var(--color-muted-foreground)',
};

/** FED-style 0–100 score → tone (prototype fedColor: ≥80 red, ≥60 amber, else teal). */
export const fedTone = (f: number): Tone => (f >= 80 ? 'bad' : f >= 60 ? 'warn' : 'good');

export const Pill: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = 'muted',
  className,
  children,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold',
      toneSurface[tone],
      className,
    )}
  >
    {children}
  </span>
);

export const Dot: React.FC<{ tone?: Tone; color?: string; className?: string }> = ({ tone, color, className }) => (
  <span
    className={cn('inline-block h-2 w-2 shrink-0 rounded-full', tone && toneBg[tone], className)}
    style={color ? { background: color } : undefined}
  />
);

/** Headline number tile. `tone` tints the whole tile (prototype .kpi.alert/.warn/.good). */
export const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
  /** Colour the sub line: 'up' = worse (destructive), 'dn' = better (success). */
  subTrend?: 'up' | 'dn';
  className?: string;
  onClick?: () => void;
}> = ({ label, value, sub, tone, subTrend, className, onClick }) => (
  <Card
    onClick={onClick}
    className={cn(
      'p-4',
      tone && tone !== 'muted' && toneSurface[tone].replace(/text-\S+/, ''),
      onClick && 'cursor-pointer',
      className,
    )}
  >
    <div className='mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>{label}</div>
    <div className='text-[28px] font-bold leading-none tabular-nums'>{value}</div>
    {sub != null && (
      <div
        className={cn(
          'mt-1 text-xs text-muted-foreground',
          subTrend === 'up' && 'text-destructive',
          subTrend === 'dn' && 'text-success',
        )}
      >
        {sub}
      </div>
    )}
  </Card>
);

export const KpiRow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3', className)}>{children}</div>
);

/** Card with the prototype's header row (title + subtitle + right-side actions). */
export const Panel: React.FC<{
  title?: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, sub, actions, className, children }) => (
  <Card className={cn('p-4', className)}>
    {(title || actions) && (
      <div className='mb-3.5 flex items-center justify-between gap-3'>
        <div className='min-w-0'>
          {title && <div className='text-[13px] font-semibold'>{title}</div>}
          {sub && <div className='text-xs text-muted-foreground'>{sub}</div>}
        </div>
        {actions && <div className='flex shrink-0 items-center gap-2'>{actions}</div>}
      </div>
    )}
    {children}
  </Card>
);

export const SecHeader: React.FC<{ children: React.ReactNode; actions?: React.ReactNode; className?: string }> = ({
  children,
  actions,
  className,
}) => (
  <div
    className={cn(
      'mb-3 flex items-center gap-2 border-b border-border pb-2 text-[13px] font-semibold text-muted-foreground',
      className,
    )}
  >
    {children}
    {actions && <div className='ml-auto flex gap-2'>{actions}</div>}
  </div>
);

/** Responsive grids from the prototype (.grid-2/.grid-3/.grid-1-2). */
export const Grid: React.FC<{ cols: 2 | 3 | '1-2'; className?: string; children: React.ReactNode }> = ({
  cols,
  className,
  children,
}) => (
  <div
    className={cn(
      'grid grid-cols-1 gap-4',
      cols === 2 && 'lg:grid-cols-2',
      cols === 3 && 'lg:grid-cols-3',
      cols === '1-2' && 'lg:grid-cols-[1fr_2fr]',
      className,
    )}
  >
    {children}
  </div>
);

/** Selectable chip (range / filter pickers). */
export const Chip: React.FC<{ active?: boolean; onClick?: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type='button'
    onClick={onClick}
    className={cn(
      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
      active
        ? 'border-success/30 bg-success/10 text-success'
        : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
    )}
  >
    {children}
  </button>
);

/** Segmented tab group (prototype .drill-nav / .drill-btn). */
export function Tabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: React.ReactNode }[];
}) {
  return (
    <div className='flex flex-wrap items-center gap-2'>
      {options.map((o) => (
        <button
          key={o.key}
          type='button'
          onClick={() => onChange(o.key)}
          className={cn(
            'flex items-center gap-1.5 rounded-lg border px-3.5 py-1.5 text-[13px] font-medium transition-colors',
            value === o.key
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Thin horizontal score bar (prototype .fbar). */
export const FBar: React.FC<{ value: number; max?: number; tone?: Tone; color?: string; className?: string }> = ({
  value,
  max = 100,
  tone = 'good',
  color,
  className,
}) => (
  <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}>
    <div
      className={cn('h-full rounded-full', !color && toneBg[tone])}
      style={{ width: `${Math.max(0, Math.min(100, (value / max) * 100))}%`, background: color }}
    />
  </div>
);

export const Banner: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = 'bad',
  className,
  children,
}) => (
  <div
    className={cn('flex items-center gap-2 rounded-lg border px-3.5 py-2.5 text-[13px]', toneSurface[tone], className)}
  >
    {children}
  </div>
);

/** Small score chip used in the FED / CDI / LRM trio. */
export const ScoreChip: React.FC<{ label: string; value: React.ReactNode; sub?: string; color?: string }> = ({
  label,
  value,
  sub,
  color,
}) => (
  <div className='flex min-w-[52px] flex-col items-center gap-px rounded-lg border border-border bg-muted px-2.5 py-1.5'>
    <span className='text-lg font-bold leading-tight tabular-nums' style={{ color }}>
      {value}
    </span>
    <span className='text-[9px] font-bold uppercase tracking-wider text-muted-foreground'>{label}</span>
    {sub && <span className='text-[9px] leading-none text-muted-foreground'>{sub}</span>}
  </div>
);

/** Plain table styling matching the prototype (11px uppercase headers, hover rows). */
export const dataTable = {
  wrap: 'overflow-x-auto',
  table: 'w-full border-collapse text-xs',
  th: 'whitespace-nowrap border-b border-border px-2.5 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground',
  td: 'border-b border-border px-2.5 py-2 align-middle group-last:border-b-0',
  num: 'tabular-nums text-right',
  tr: 'group hover:bg-muted/60',
} as const;
